import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    inject
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { HeaderComponent } from "@components/header/header.component";
import { IconComponent } from "@components/icon/icon.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { Observable, Subject, of } from "rxjs";
import {
    debounceTime,
    distinctUntilChanged,
    map,
    share,
    takeUntil
} from "rxjs/operators";
import { IMessageModel } from "~app/common/components/message-panel/message-panel.models";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { DisabledControlDirective } from "../../../../../../common/directives/disabled-control/disabled-control.directive";
import { AtgardNaturvardesbedomningDTO } from "../../../../models/naturvardesbedomning-models";
import { VirkeskopValidationMessageComponent } from "../../../../validation/validation-message/virkeskop-validation-message.component";

@Component({
    selector: "rask-nvbnorra-sverige",
    templateUrl: "./nvbnorra-sverige.component.html",
    styleUrls: ["./nvbnorra-sverige.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        HeaderComponent,
        InputNumberComponent,
        CheckboxComponent,
        VirkeskopValidationMessageComponent,
        IconComponent,
        ButtonComponent,
        MessagePanelComponent,
        RadiobuttonGroupComponent,
        TextareaComponent,
        DisabledControlDirective,
        AsyncPipe
    ]
})
export class NVBNorraSverigeComponent implements OnChanges, OnInit {
    atgardGuid$: Observable<string>;
    checkboxNames = [
        "naturvardestrad",
        "naturvardestradKvalitet",
        "dodVed",
        "dodVedKvalitet",
        "alder",
        "kontinuitet",
        "tradslag",
        "hansynskravandeBiotop"
    ];
    form: UntypedFormGroup;
    isSummed = false;
    @Input() naturvardesbedomning: AtgardNaturvardesbedomningDTO;
    @Input() readonly = false;
    showDetailedDeadWood = false;
    showSecondForm = false;
    sumItems: Observable<IMessageModel[]>;
    sumText = "";
    @Output() updateDTO: EventEmitter<any> = new EventEmitter();

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly authService = inject(AuthenticationService);
    private readonly formBinder = inject(FormBinderService);
    private readonly formRebind$ = new Subject<void>();

    aggregateValues() {
        this.isSummed = true;
        this.showSecondForm = true;
        const values = [
            this.form.controls.naturvardestrad.value,
            this.form.controls.naturvardestradKvalitet.value,
            this.form.controls.dodVed.value,
            this.form.controls.dodVedKvalitet.value,
            this.form.controls.alder.value,
            this.form.controls.kontinuitet.value,
            this.form.controls.tradslag.value
        ];

        if (this.form.controls.hansynskravandeBiotop.value === true) {
            this.sumText = "Alltid skyddad enligt lag, ska avsättas som naturvård";
        } else {
            const value = values.filter(Boolean).length;
            switch (value) {
                case 0:
                    this.sumText = "Naturvärden kan sannolikt tas om hand genom generell eller förstärkt miljöhänsyn";
                    break;
                case 1:
                    this.sumText = "Vissa naturvärden finns. Naturvärden kan troligen tas om hand genom generell eller förstärkt miljöhänsyn";
                    break;
                case 2:
                    this.sumText = "Vissa naturvärden finns. Möjligen höga naturvärden, använd second opinion vid behov";
                    break;
                default: //3 eller fler
                    this.sumText = "Det finns höga naturvärden, sannolikt nyckelbiotop";
                    break;
            }
        }
        this.sumItems = of([
            {
                severity: 4,
                message: this.sumText,
                anchor: ""
            }
        ]);
    }

    canAggregate(): boolean {
        return this.hasValueAboveNegativeOne(this.form.controls.naturvardestradTotalPerHa.value) &&
            this.hasValueAboveNegativeOne(this.form.controls.naturvardestradKvalitetTotalPerHa.value) &&
            this.hasValueAboveNegativeOne(this.form.controls.dodVedTotalPerHa.value) &&
            this.hasValueAboveNegativeOne(this.form.controls.dodVedKvalitetTotalPerHa.value) &&
            (this.showDetailedDeadWood && this.hasValueAboveNegativeOne(this.form.controls.dodVedKvalitetTrslPerHa.value) || !this.showDetailedDeadWood);
    }

    canSave() {
        return this.form.controls.hansynMiljovarden.value &&
        this.hasValueAboveNegativeOne(this.form.controls.naturvardestradTotalPerHa.value) &&
        this.hasValueAboveNegativeOne(this.form.controls.naturvardestradKvalitetTotalPerHa.value) &&
        this.hasValueAboveNegativeOne(this.form.controls.dodVedTotalPerHa.value) &&
        this.hasValueAboveNegativeOne(this.form.controls.dodVedKvalitetTotalPerHa.value) &&
        !ObjectUtils.isNullOrUndefined(this.form.controls.bedomningSno.value) &&
        (this.showDetailedDeadWood && this.hasValueAboveNegativeOne(this.form.controls.dodVedKvalitetTrslPerHa.value) || !this.showDetailedDeadWood);
    }

    checkDodVedKvalitet() {
        if (this.form.controls.dodVedKvalitetTotalPerHa.value >= 15) {
            this.patchValueIfDifferent("dodVedKvalitet", true);

        } else if (this.form.controls.dodVedKvalitetTrslPerHa.value >= 20) {
            this.patchValueIfDifferent("dodVedKvalitet", true);
        } else {
            this.patchValueIfDifferent("dodVedKvalitet", false);
        }
    }

    checkSumOnInit() {
        if (this.canAggregate()) {
            this.aggregateValues();
        }
    }

    checkboxIsChanged() {
        this.isSummed = false;
        this.sumText = "";
    }

    detectAllChanges() {
        this.form.valueChanges.pipe(
            debounceTime(200),
            distinctUntilChanged()).pipe(
            takeUntil(this.formRebind$)
        ).subscribe(change => {
            this.updateDTO.emit();
        });
    }

    detectCheckboxChanges() {
        this.checkboxNames.forEach(cb => {
            this.form.get(cb).valueChanges.pipe(
                takeUntil(this.formRebind$)
            ).subscribe(change => {
                this.checkboxIsChanged();
            });
        });
    }

    dodVedChanged() {
        this.uncheckUtfordIfInvalid();
        if (this.form.controls.dodVedTotalPerHa.value >= 30) {
            this.patchValueIfDifferent("dodVed", true);
        } else {
            this.patchValueIfDifferent("dodVed", false);
        }
    }

    dodVedKvalitetChanged() {
        this.uncheckUtfordIfInvalid();
        this.showHideSilverWood();
        this.checkDodVedKvalitet();
    }

    dodVedKvalitetSilverChanged() {
        this.uncheckUtfordIfInvalid();
        this.checkDodVedKvalitet();
    }

    hasValueAboveNegativeOne(value) {
        if (value === null || value === undefined) {
            return false;
        }

        if (value > -1) {
            return true;
        }
        return false;
    }

    // Kan användas för validering (från validation):
    // naturvardestradTotalPerHa, naturvardestradKvalitetTotalPerHa,
    // dodVedTotalPerHa, dodVedKvalitetTotalPerHa, dodVedKvalitetTrslPerHa
    isNumericIntegerGreaterThanNegativeOne(value: any) {
        const parsed = Number.parseInt(value, 10);
        return !Number.isNaN(parsed) && Number.isInteger(value) && parsed > -1;
    }

    naturvardeChanged() {
        this.uncheckUtfordIfInvalid();
        if (this.form.controls.naturvardestradTotalPerHa.value >= 20) {
            this.patchValueIfDifferent("naturvardestrad", true);
        } else {
            this.patchValueIfDifferent("naturvardestrad", false);
        }
    }

    naturvardeKvalitetChanged() {
        this.uncheckUtfordIfInvalid();
        if (this.form.controls.naturvardestradKvalitetTotalPerHa.value >= 10) {
            this.patchValueIfDifferent("naturvardestradKvalitet", true);
        } else {
            this.patchValueIfDifferent("naturvardestradKvalitet", false);
        }
    }

    ngOnChanges(): void {
        this.formRebind$.next();
        this.form = this.formBinder.CreateGroup(this.naturvardesbedomning);
        this.form.get("dodVedKvalitet").disable();
        this.form.get("naturvardestrad").disable();
        this.form.get("dodVed").disable();
        this.form.get("naturvardestradKvalitet").disable();

        if (this.readonly) {
            this.form.disable();
        }

        this.form.markAsPristine();
        this.detectAllChanges();
        this.detectCheckboxChanges();
        this.showHideSilverWood();
        this.checkSumOnInit();
    }

    ngOnInit(): void {
        this.atgardGuid$ = this.atgardMediatorService.atgard$.pipe(
            map(atgard => atgard.guid),
            share()
        );
    }

    patchValueIfDifferent(control: string, newValue: any) {
        if (this.form.get(control).value !== newValue &&
            !(ObjectUtils.isNullOrUndefined(this.form.get(control).value) &&  ObjectUtils.isNullOrUndefined(newValue))) {
            this.form.get(control).patchValue(newValue);
        }
    }

    showHideSilverWood() {
        const value = this.form.controls.dodVedKvalitetTotalPerHa.value;
        if (value >= 10 && value <= 14) {
            this.showDetailedDeadWood = true;
        } else {
            this.showDetailedDeadWood = false;
            this.patchValueIfDifferent("dodVedKvalitetTrslPerHa", undefined);
        }
    }

    uncheckUtfordIfInvalid() {
        if (!this.canSave()) {
            this.form.controls.naturvardesbedomningUtford.patchValue(false);
        }
    }

    utfordChanged(checked) {
        if (checked) {
            this.form.controls.utfordDatum.patchValue(new Date());
            this.form.controls.utfordAv.patchValue(this.authService.getUserData().fullUserName);
        } else {
            this.form.controls.utfordDatum.patchValue(null);
            this.form.controls.utfordAv.patchValue(null);
        }
    }
}
